import { useState } from "react";
import {
  Megaphone,
  Clapperboard,
  Target,
  PenTool,
  Monitor,
  Camera,
  ShieldCheck,
  BarChart3,
  LineChart,
  Users,
  ArrowUpRight,
  Check,
} from "lucide-react";
import { motion } from "framer-motion";
import { SERVICES } from "../data/landing";
import { useTilt } from "../components/useTilt";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const EASE = [0.22, 1, 0.36, 1];
const CARD_REVEAL = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};
const cardTransition = (i) => ({ duration: 0.55, delay: (i % 3) * 0.08, ease: EASE });

const ICONS = {
  Megaphone,
  Clapperboard,
  Target,
  PenTool,
  Monitor,
  Camera,
  ShieldCheck,
  BarChart3,
  LineChart,
  Users,
};

function ServiceCard({ s, i, onOpen }) {
  const Icon = ICONS[s.icon];
  const tilt = useTilt(6);
  return (
    <motion.article
      {...CARD_REVEAL}
      transition={cardTransition(i)}
      whileHover={{ y: -8 }}
      style={tilt.style}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
      onClick={onOpen}
      className="service-card-light group cursor-pointer relative flex flex-col transition-shadow duration-300 hover:shadow-[0_30px_70px_-24px_rgba(20,64,225,0.6)]"
      data-testid={`service-card-${i}`}
    >
      <div className="relative">
        <div className="relative h-40 overflow-hidden">
          <img
            src={s.image}
            alt={s.title}
            loading="lazy"
            className="w-full h-full object-cover brightness-95 group-hover:brightness-105 group-hover:scale-[1.1] transition-all duration-[900ms] ease-out"
            data-testid={`service-image-${i}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/25 to-transparent" />
        </div>
        <div className="absolute -bottom-6 left-6 size-12 rounded-xl bg-[#1440E1] border border-[#1440E1] flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(20,64,225,0.6)] z-20 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
          {Icon ? <Icon size={22} className="text-white" strokeWidth={2.25} /> : null}
        </div>
      </div>
      <div className="p-7 sm:p-8 pt-9 flex flex-col relative z-10">
        <h3 className="font-arial-bold text-xl text-black mb-3 leading-tight">
          {s.title}
        </h3>
        <p className="text-sm text-black/65 leading-relaxed mb-6">{s.desc}</p>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-black/50 group-hover:text-[#1440E1] group-hover:gap-3 transition-all mt-auto">
          Learn more
          <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform duration-300" />
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  const [active, setActive] = useState(null);
  const svc = active !== null ? SERVICES[active] : null;
  const ActiveIcon = svc ? ICONS[svc.icon] : null;
  return (
    <section
      id="services"
      className="relative bg-[#050B26] py-24 sm:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Soft radial accents */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(800px 480px at 8% 10%, rgba(255,255,255,0.18) 0%, transparent 60%), radial-gradient(700px 400px at 92% 90%, rgba(10,17,48,0.55) 0%, transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/80 font-semibold mb-4">
              Our Services
            </p>
            <h2 className="font-arial-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight max-w-2xl">
              Nine disciplines.
              <span className="block text-white/55">One growth engine.</span>
            </h2>
          </div>
          <p className="text-white/85 max-w-md leading-relaxed">
            Every service is designed to compound — strategy informs creative,
            creative powers content, and content fuels measurable growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} s={s} i={i} onOpen={() => setActive(i)} />
          ))}
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-lg bg-[#0A1130] border border-white/10 text-white p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
          data-testid="service-modal"
        >
          {svc ? (
            <>
              <div className="relative h-44">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1130] via-[#0A1130]/40 to-transparent" />
                <div className="absolute bottom-4 left-6 size-12 rounded-xl bg-[#1440E1] flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(20,64,225,0.6)]">
                  {ActiveIcon ? (
                    <ActiveIcon size={22} className="text-white" strokeWidth={2.25} />
                  ) : null}
                </div>
              </div>
              <div className="px-6 sm:px-8 pb-8 pt-2">
                <DialogHeader>
                  <DialogTitle className="font-arial-bold text-2xl text-white">
                    {svc.title}
                  </DialogTitle>
                  <DialogDescription className="text-white/70 text-sm leading-relaxed pt-1">
                    {svc.desc}
                  </DialogDescription>
                </DialogHeader>

                <p className="mt-6 mb-3 text-[11px] uppercase tracking-[0.28em] text-[#5b80ff] font-semibold">
                  What&rsquo;s Included
                </p>
                <ul className="space-y-2.5">
                  {svc.included?.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-white/85"
                    >
                      <span className="mt-0.5 size-5 shrink-0 rounded-full bg-[#1440E1]/20 border border-[#1440E1]/50 flex items-center justify-center">
                        <Check size={12} className="text-[#5b80ff]" strokeWidth={3} />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>

                {svc.samples?.length ? (
                  <>
                    <p className="mt-7 mb-3 text-[11px] uppercase tracking-[0.28em] text-[#5b80ff] font-semibold">
                      Sample Work
                    </p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {svc.samples.map((src, si) => (
                        <div
                          key={si}
                          className="aspect-square rounded-xl overflow-hidden bg-black/40 border border-white/10"
                        >
                          <img
                            src={src}
                            alt={`${svc.title} sample ${si + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}

                <a
                  href="#get-started"
                  onClick={() => setActive(null)}
                  className="btn-kmg mt-8 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 font-bold tracking-wide text-white bg-[#1440E1] hover:bg-[#1033B3] transition-colors"
                  data-testid="service-modal-cta"
                >
                  Start This Service
                  <ArrowUpRight size={18} />
                </a>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
